import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Download, Phone, Mail, MapPin, Cake, Heart, UserPlus2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadUrl, API_BASE } from "@/lib/api";


/**
 * Public read-only visiting card served at /card/:share_token.
 *
 * Deliberately doesn't require auth — anyone with the QR/URL can view. The
 * `share_token` is separate from the member's edit `token`, so scanning the QR
 * does NOT expose edit access.
 */
export default function VisitingCard() {
    const { token } = useParams();
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_BASE}/card/${token}`);
                setMember(data);
            } catch (e) {
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const downloadVCard = async () => {
        try {
            const resp = await axios.get(`${API_BASE}/card/${token}/vcf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/vcard" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `${(member?.name || "member").replace(/[^A-Za-z0-9]+/g, "_")}.vcf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Contact card downloaded — open the file to add to your phone");
        } catch (e) {
            toast.error("Could not download contact card");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rotary-blue/5 via-white to-rotary-gold/10">
                <p className="text-sm text-muted-foreground">Loading card…</p>
            </div>
        );
    }
    if (notFound || !member) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rotary-blue/5 via-white to-rotary-gold/10">
                <div className="text-center p-8 max-w-md">
                    <h1 className="rotary-heading text-2xl text-rotary-ink mb-2">Card not found</h1>
                    <p className="text-sm text-muted-foreground">This link is invalid or the member hasn&apos;t finished their profile yet.</p>
                </div>
            </div>
        );
    }

    const photoUrl = member.photo_filename ? uploadUrl(member.photo_filename, member.updated_at) : "";

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rotary-blue/5 via-white to-rotary-gold/10 p-4">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border overflow-hidden" data-testid="visiting-card">
                {/* Header banner */}
                <div className="relative h-24 bg-gradient-to-r from-rotary-blue to-rotary-sky">
                    <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
                </div>

                <div className="px-6 -mt-12 pb-6">
                    {/* Avatar */}
                    <div className="mb-3">
                        {photoUrl ? (
                            <img
                                src={photoUrl}
                                alt={member.name}
                                className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-md"
                                data-testid="card-photo"
                            />
                        ) : (
                            <div className="h-24 w-24 rounded-full bg-rotary-blue/10 border-4 border-white shadow-md flex items-center justify-center text-2xl font-semibold text-rotary-blue">
                                {(member.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("")}
                            </div>
                        )}
                    </div>

                    {/* Identity */}
                    <div>
                        <h1 className="rotary-heading text-2xl text-rotary-ink leading-tight" data-testid="card-name">
                            {member.name}
                        </h1>
                        {(member.role_display || member.position) && (
                            <p
                                className="mt-0.5 text-sm text-rotary-blue font-medium"
                                data-testid="card-role"
                            >
                                {member.role_display || member.position}
                            </p>
                        )}
                        {member.club_name && (
                            <p className="mt-1 text-xs text-muted-foreground flex items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5" />
                                {member.club_name}
                                {member.ri_district && <span className="opacity-60">· {member.ri_district}</span>}
                            </p>
                        )}
                    </div>

                    <hr className="my-5 border-slate-100" />

                    {/* Details */}
                    <dl className="space-y-3 text-sm">
                        {member.phone && (
                            <Row icon={<Phone className="h-4 w-4" />} label="Phone">
                                <a href={`tel:${member.phone}`} className="text-rotary-blue hover:underline">{member.phone}</a>
                            </Row>
                        )}
                        {member.email && (
                            <Row icon={<Mail className="h-4 w-4" />} label="Email">
                                <a href={`mailto:${member.email}`} className="text-rotary-blue hover:underline break-all">{member.email}</a>
                            </Row>
                        )}
                        {member.postal_address && (
                            <Row icon={<MapPin className="h-4 w-4" />} label="Address">
                                <span className="whitespace-pre-line">{member.postal_address}</span>
                            </Row>
                        )}
                        {member.birthday && (
                            <Row icon={<Cake className="h-4 w-4" />} label="Birthday">
                                {member.birthday}
                            </Row>
                        )}
                        {member.spouse_name && (
                            <Row icon={<Heart className="h-4 w-4" />} label="Spouse">
                                {member.spouse_name}
                                {member.anniversary && <span className="text-muted-foreground text-xs ml-2">· Anniversary {member.anniversary}</span>}
                            </Row>
                        )}
                    </dl>

                    {/* Actions */}
                    <div className="mt-6">
                        <Button
                            onClick={downloadVCard}
                            className="w-full rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                            data-testid="save-contact-btn"
                        >
                            <UserPlus2 className="mr-2 h-4 w-4" />
                            Save to contacts
                        </Button>
                    </div>
                </div>

                {/* Footer with tiny QR (so someone can re-share this same URL) */}
                <div className="border-t bg-slate-50 px-6 py-4 flex items-center gap-3">
                    <div className="bg-white rounded-lg p-1.5 border">
                        <QRCodeSVG value={window.location.href} size={56} level="M" includeMargin={false} />
                    </div>
                    <div className="text-[11px] text-muted-foreground leading-tight">
                        <p className="font-medium text-rotary-ink">Share this card</p>
                        <p>Scan or forward the link — anyone can view, but only {member.name.split(" ")[0]} can edit their profile.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ icon, label, children }) {
    return (
        <div className="flex items-start gap-3">
            <div className="mt-0.5 text-muted-foreground shrink-0">{icon}</div>
            <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
                <div className="text-sm text-rotary-ink">{children}</div>
            </div>
        </div>
    );
}
